import { PageLayout } from '@kcftech/design-system';
import { LocationsMultiSelect } from 'components/reports/LocationsMultiSelect';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { BarChart, PieChart, ResponsiveContainer } from 'recharts';
import styled, { css } from 'styled-components';
import {
    backgroundColor,
    backgroundColorActive,
    bodyBackgroundColor,
    borderColor,
    dashboardAccentPrimary,
    dashboardAxisText,
    navBorderColor,
    primaryText,
    secondaryText,
} from 'styles/theme';

const cardContainerStyles = css`
    border-radius: 12px;
    background-color: ${backgroundColor};
    outline: 1px solid ${borderColor};
    padding: 1.6rem;
`;

export const Card = styled.div`
    ${cardContainerStyles}
    display: grid;
    gap: 1rem;
    grid-template-rows: min-content 1fr;
    & > :not(:first-child) {
        justify-self: stretch;
        align-self: stretch;
    }
`;

export const CardTitle = styled.h2`
    color: ${primaryText};
    font-size: 1.7rem;
    font-weight: 600;
    line-height: 2rem;
    margin: 0;
    padding-bottom: 0.8rem;
    border-bottom: 1px solid ${borderColor};
`;

export const CardContent = styled.div<{
    $contentPositioning: 'center' | 'stretch' | undefined;
}>`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    ${({ $contentPositioning }) =>
        $contentPositioning !== undefined
            ? `
align-items: ${$contentPositioning};
justify-content: ${$contentPositioning};
    `
            : ''}
    overflow: hidden;
    position: relative;
`;

/**
 * Dashboard header with logo and filters
 */
export const Header = styled.header`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    padding: 1.8rem;
    border-radius: 8px;
    border: 1px solid ${borderColor};
    background-color: ${backgroundColor};

    @media ${MediaQueries.largeAndDown} {
        flex-direction: column;
        align-items: flex-start;
        gap: 2rem;
    }
`;

export const HeaderControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 2rem;

    @media ${MediaQueries.largeAndDown} {
        flex-direction: column;
        align-items: stretch;
        width: 100%;
        gap: 1.5rem;
    }
`;

export const QuickFilterHeaderRegion = styled.div`
    border: 1px solid ${borderColor};
    border-radius: 8px;
    padding: 0.5rem;
    display: flex;
    gap: 1rem;
    & > * {
        background-color: ${backgroundColor};
        border-color: ${borderColor};
        border-radius: 8px;
        &:active {
            outline: 1px solid ${dashboardAccentPrimary};
        }
    }
`;

export const StyledLocationsMultiSelect = styled(LocationsMultiSelect)`
    width: 28rem;
    padding-bottom: 0;

    @media ${MediaQueries.largeAndDown} {
        width: 100%;
    }
`;

export const StyledRechartsResponsiveContainer = styled(ResponsiveContainer)`
    text {
        fill: ${primaryText};
    }

    .recharts-cartesian-grid line {
        stroke: ${navBorderColor};
    }

    line.recharts-cartesian-axis-line
    /* .recharts-cartesian-axis-tick line  */ {
        stroke: ${secondaryText};
    }

    .recharts-tooltip-cursor {
        fill: ${backgroundColorActive};
        fill-opacity: 90%;
    }

    .recharts-tooltip-wrapper {
        fill: blue;
    }

    .recharts-default-tooltip {
        background-color: ${backgroundColor} !important;
        border: 1px solid ${navBorderColor} !important;
        border-radius: 5px;
    }
    .recharts-tooltip-label {
        fill: ${primaryText};
        color: ${primaryText};
    }

    // bar labels
    .recharts-layer .recharts-label-list text.recharts-text {
        font-size: 1.2rem;
    }

    text,
    .recharts-text.recharts-cartesian-axis-tick-value,
    .recharts-text.recharts-cartesian-axis-tick-value tspan {
        color: ${dashboardAxisText};
        fill: ${dashboardAxisText};
        font-size: 1.4rem;
    }
`;

export const StyledPieChart = styled(PieChart)`
    text {
        fill: ${primaryText};
    }

    path.recharts-sector {
        stroke: ${borderColor};
    }
`;

export const DashboardPageLayout = styled(PageLayout.Primary)`
    padding-top: 1.6rem;
    padding-bottom: 1.6rem;
    box-sizing: border-box;
    background-color: ${bodyBackgroundColor};
    display: grid;
    gap: 1.2rem;

    .head {
        ${cardContainerStyles}
        grid-area: head;
        align-content: center;
        & h1 {
            font-size: 2.4rem;
        }
    }
`;

/**
 * Shared chart styled components
 */

export const ChartContainer = styled.div`
    width: 100%;
    height: 100%;
    min-height: 20rem;
`;

export const StyledBarChart = styled(BarChart)`
    text {
        fill: ${primaryText};
    }
`;

export const CenteredChartContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const PieChartLegendContainer = styled.div<{
    $gap?: string;
    $justifyContent?: string;
}>`
    display: flex;
    flex-direction: column;
    gap: ${({ $gap }) => $gap || '1rem'};
    padding: 1rem;
    ${({ $justifyContent }) => $justifyContent && `justify-content: ${$justifyContent};`}
`;

export const PieChartLegendItem = styled.div<{
    $color: string;
    $variant?: 'circle' | 'square';
    $gap?: string;
}>`
    display: flex;
    align-items: center;
    gap: ${({ $gap }) => $gap || '0.8ch'};
    color: ${secondaryText};
    font-size: 1.4rem;

    &::before {
        content: '';
        display: block;
        width: 1.6rem;
        height: 1.6rem;
        border-radius: ${({ $variant }) => ($variant === 'square' ? '4px' : '50%')};
        background-color: ${({ $color }) => $color};
    }
`;
